/**
 * A customer's file.
 *
 * The page this replaces showed nine label/value rows and a list of pet names.
 * An operator taking a support call about a refund could learn the customer's
 * email and nothing about their bookings, their spend, their addresses, or
 * whether they had been refunded before — all of which lived one module away
 * behind a filter that could not be expressed.
 *
 * Now: identity and standing at the top, money and activity as metrics, then
 * tabs for the records themselves, each linking out to the full filtered list.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  Ban,
  Bell,
  CalendarCheck2,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Flag,
  Home,
  Mail,
  MapPin,
  MessageSquare,
  PawPrint,
  Phone,
  Receipt,
  ShieldAlert,
  ShoppingBag,
  Star,
  Store,
  UserCog,
  Wallet,
} from 'lucide-react';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import {
  DetailPanel,
  Field,
  FieldList,
  PanelEmpty,
  PanelSkeleton,
} from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { ConfirmModal } from '@/features/vendors/components/reason-modal';
import { notify } from '@/components/ui/toaster';
import { string_param, use_url_state } from '@/hooks/use-url-state';
import { ago, date_short, date_time, inr, person_name, title_case } from '@/lib/format';
import { use_set_user_active, use_user_detail } from '@/features/users/hooks';
import { use_set_pet_active } from '@/features/pets/hooks';
import { ReasonModal } from '@/features/vendors/components/reason-modal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ExternalLink, FileText, MoreHorizontal, Stethoscope, Syringe } from 'lucide-react';
import type { UserDetail, UserPetSummary } from '@/features/users/types';
import { cn } from '@/lib/utils';

const TABS = ['overview', 'pets', 'bookings', 'orders', 'reviews'] as const;
type Tab = (typeof TABS)[number];

/** Status → badge tone, shared by the booking and order tables below. */
function status_tone(status: string): 'success' | 'warning' | 'destructive' | 'info' | 'neutral' {
  const s = status.toUpperCase();
  if (s === 'COMPLETED' || s === 'DELIVERED' || s === 'CAPTURED') return 'success';
  if (s === 'CANCELLED' || s === 'NO_SHOW' || s === 'FAILED') return 'destructive';
  if (s.startsWith('PENDING') || s === 'AWAITING_VENDOR') return 'warning';
  if (s === 'CONFIRMED' || s === 'IN_PROGRESS' || s === 'SHIPPED') return 'info';
  return 'neutral';
}

export function UserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { data: u, isLoading, error, refetch } = use_user_detail(id);
  const set_active_m = use_set_user_active(id);
  const [confirm_open, set_confirm_open] = useState(false);
  const [action_error, set_action_error] = useState<string | null>(null);
  // The tab lives in the URL so "look at their bookings" is a link you can send.
  const [tab_raw, set_tab] = use_url_state('tab', string_param);
  const tab: Tab = (TABS as readonly string[]).includes(tab_raw) ? (tab_raw as Tab) : 'overview';

  const named = Boolean(u?.first_name || u?.last_name);
  const display = u ? (named ? person_name(u) : (u.phone ?? u.email ?? 'Customer')) : 'Customer';

  if (error) {
    return (
      <DashboardLayout title="Customer" breadcrumbs={[{ label: 'Not found' }]}>
        <ErrorState
          title="Couldn't load this customer"
          description={error instanceof Error ? error.message : 'The request failed.'}
          action={
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <Link to="/users">Back to customers</Link>
              </Button>
            </div>
          }
        />
      </DashboardLayout>
    );
  }

  const a = u?.activity;

  const metrics: Metric[] = [
    {
      key: 'spend',
      label: 'Lifetime spend',
      value: a ? inr(a.lifetime_spend_inr) : undefined,
      hint: a && Number(a.refunded_inr) > 0 ? `${inr(a.refunded_inr)} refunded` : 'Net of refunds',
      icon: Wallet,
      tone: 'success',
      loading: isLoading,
    },
    {
      key: 'bookings',
      label: 'Bookings',
      value: a?.bookings_total,
      hint:
        a && a.bookings_cancelled > 0 ? `${a.bookings_cancelled} cancelled or no-show` : undefined,
      icon: CalendarCheck2,
      tone: a && a.bookings_cancelled > 0 ? 'warning' : 'default',
      to: `/bookings?user_id=${id}`,
      loading: isLoading,
    },
    {
      key: 'orders',
      label: 'Orders',
      value: a?.orders_total,
      icon: ShoppingBag,
      to: `/commerce-orders?user_id=${id}`,
      loading: isLoading,
    },
    {
      key: 'pets',
      label: 'Pets',
      value: u?.pets.length,
      icon: PawPrint,
      loading: isLoading,
    },
    {
      key: 'reviews',
      label: 'Reviews',
      value: a?.reviews_total,
      icon: Star,
      loading: isLoading,
    },
  ];

  return (
    <DashboardLayout
      title={display}
      subtitle={
        u
          ? `${title_case(u.role)} · joined ${date_short(u.created_at)}${u.last_login_at ? ` · last seen ${ago(u.last_login_at)}` : ' · never signed in'}`
          : undefined
      }
      breadcrumbs={[{ label: display }]}
      actions={
        u && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(u.id);
                notify.success('User ID copied');
              }}
              data-testid="copy-user-id"
            >
              <Copy className="h-4 w-4" /> Copy ID
            </Button>
            {u.vendor_profile_id && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/vendors/${u.vendor_profile_id}`} data-testid="user-vendor-link">
                  <Store className="h-4 w-4" /> Vendor profile
                </Link>
              </Button>
            )}
            <Button
              variant={u.is_active ? 'destructive' : 'default'}
              size="sm"
              data-testid="user-toggle-active"
              // Blocking a vendor account is refused by the API — say so here
              // rather than letting the operator discover it from a 422.
              disabled={u.is_active && u.role === 'VENDOR'}
              title={
                u.is_active && u.role === 'VENDOR'
                  ? 'Vendor accounts are suspended from their vendor page'
                  : undefined
              }
              onClick={() => {
                set_action_error(null);
                set_confirm_open(true);
              }}
            >
              {u.is_active ? (
                <>
                  <Ban className="h-4 w-4" /> Block
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" /> Unblock
                </>
              )}
            </Button>
          </>
        )
      }
      avatar={
        <PersonAvatar
          path={u?.avatar_url ?? null}
          name={named ? display : null}
          seed={u?.id}
          size="lg"
          fallback_icon={UserCog}
        />
      }
      title_badges={
        u && (
          <>
            {u.is_active ? (
              <Badge tone="success" dot>
                Active
              </Badge>
            ) : (
              <Badge tone="destructive" dot>
                Blocked
              </Badge>
            )}
            <Badge
              tone={u.role === 'ADMIN' ? 'warning' : u.role === 'VENDOR' ? 'primary' : 'neutral'}
            >
              {title_case(u.role)}
            </Badge>
            {!named && (
              <Badge tone="neutral" variant="outline">
                No name on file
              </Badge>
            )}
          </>
        )
      }
      meta={u && <ContactMeta user={u} />}
      stats={<MetricStrip metrics={metrics} />}
      tabs={
        <PageTabs
          current={tab}
          on_change={(v) => set_tab(v)}
          tabs={[
            { value: 'overview', label: 'Overview' },
            { value: 'pets', label: 'Pets', count: u?.pets.length },
            { value: 'bookings', label: 'Bookings', count: a?.bookings_total },
            { value: 'orders', label: 'Orders', count: a?.orders_total },
            { value: 'reviews', label: 'Reviews', count: a?.reviews_total },
          ]}
        />
      }
    >
      {tab === 'overview' && <OverviewTab user={u} loading={isLoading} />}
      {tab === 'pets' && <PetsTab user={u} loading={isLoading} user_id={id} />}
      {tab === 'bookings' && <BookingsTab user={u} loading={isLoading} user_id={id} />}
      {tab === 'orders' && <OrdersTab user={u} loading={isLoading} user_id={id} />}
      {tab === 'reviews' && <ReviewsTab user={u} loading={isLoading} />}

      {u && (
        <ConfirmModal
          open={confirm_open}
          title={u.is_active ? 'Block this account?' : 'Unblock this account?'}
          description={
            u.is_active
              ? `${display} will no longer be able to sign in or place orders. You can unblock them again from this page.`
              : `${display} will be able to sign in and use the app again.`
          }
          confirm_label={
            set_active_m.isPending ? 'Saving…' : u.is_active ? 'Block account' : 'Unblock account'
          }
          loading={set_active_m.isPending}
          error={action_error}
          on_cancel={() => set_confirm_open(false)}
          on_confirm={() =>
            set_active_m.mutate(!u.is_active, {
              onSuccess: () => {
                set_confirm_open(false);
                notify.success(u.is_active ? `${display} blocked` : `${display} unblocked`);
              },
              onError: (err) => {
                // Inline in the modal AND a toast: the modal stays open so the
                // reason is readable next to the button that failed.
                const message =
                  err instanceof Error ? err.message : 'Could not update this account';
                set_action_error(message);
                notify.error(err, 'Could not update this account.');
              },
            })
          }
        />
      )}
    </DashboardLayout>
  );
}

/* ========================================================================
   Header contact line
   ======================================================================== */

/**
 * The facts an operator reads out loud on a support call: how to reach this
 * person, whether those details are verified, and where they are.
 *
 * The unverified-phone warning sits here rather than in the Overview tab
 * because on an OTP-first platform it is the single most common explanation for
 * "I can't log in", and it should be visible before anyone clicks anything.
 */
function ContactMeta({ user }: { user: UserDetail }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-data text-muted-foreground">
      {user.phone && (
        <ContactLine icon={Phone} value={user.phone} verified={user.phone_verified} label="Phone" />
      )}
      {user.email && (
        <ContactLine icon={Mail} value={user.email} verified={user.email_verified} label="Email" />
      )}
      {user.city && (
        <span className="inline-flex items-center gap-1.5">
          <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
          {[user.city, user.state].filter(Boolean).join(', ')}
        </span>
      )}
      {!user.phone && !user.email && <span>No contact details on file</span>}
      {user.phone && !user.phone_verified && (
        <span className="inline-flex items-center gap-1.5 text-warning">
          <ShieldAlert aria-hidden="true" className="h-3.5 w-3.5" />
          Phone never verified by OTP
        </span>
      )}
    </div>
  );
}

function ContactLine({
  icon: Icon,
  value,
  verified,
  label,
}: {
  icon: typeof Phone;
  value: string;
  verified: boolean;
  label: string;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate tabular text-foreground">{value}</span>
      {verified ? (
        <BadgeCheck
          className="h-3.5 w-3.5 shrink-0 text-success"
          aria-label={`${label} verified`}
        />
      ) : (
        <span className="shrink-0 text-micro text-warning">unverified</span>
      )}
    </span>
  );
}

/* ========================================================================
   Overview
   ======================================================================== */

function OverviewTab({ user, loading }: { user: UserDetail | undefined; loading: boolean }) {
  return (
    <div className="grid items-start gap-gutter lg:grid-cols-2">
      <DetailPanel title="Account" icon={UserCog} test_id="panel-account">
        {loading || !user ? (
          <PanelSkeleton rows={6} />
        ) : (
          <FieldList>
            <Field label="User ID" value={user.id} mono />
            <Field label="Role" value={title_case(user.role)} />
            <Field
              label="Status"
              value={
                user.is_active ? (
                  <Badge tone="success" dot>
                    Active
                  </Badge>
                ) : (
                  <Badge tone="destructive" dot>
                    Blocked
                  </Badge>
                )
              }
            />
            <Field
              label="Phone"
              value={
                user.phone && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="tabular">{user.phone}</span>
                    {user.phone_verified_at ? (
                      <Badge tone="success" size="sm">
                        Verified {date_short(user.phone_verified_at)}
                      </Badge>
                    ) : (
                      <Badge tone="warning" size="sm">
                        Unverified
                      </Badge>
                    )}
                  </span>
                )
              }
              empty="No phone"
            />
            <Field
              label="Email"
              value={
                user.email && (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span className="break-all">{user.email}</span>
                    {user.email_verified_at ? (
                      <Badge tone="success" size="sm">
                        Verified {date_short(user.email_verified_at)}
                      </Badge>
                    ) : (
                      <Badge tone="warning" size="sm">
                        Unverified
                      </Badge>
                    )}
                  </span>
                )
              }
              empty="No email"
            />
            <Field label="Joined" value={date_time(user.created_at)} />
            <Field
              label="Last signed in"
              value={
                user.last_login_at
                  ? `${date_time(user.last_login_at)} (${ago(user.last_login_at)})`
                  : null
              }
              empty="Never signed in"
            />
            <Field label="Profile updated" value={date_time(user.updated_at)} />
            <Field
              label="Vendor profile"
              value={
                user.vendor_profile_id ? (
                  <Link
                    to={`/vendors/${user.vendor_profile_id}`}
                    className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                  >
                    <Store className="h-3.5 w-3.5" /> Open vendor profile
                  </Link>
                ) : null
              }
              empty="Not a vendor"
            />
          </FieldList>
        )}
      </DetailPanel>

      <div className="flex flex-col gap-gutter">
        <DetailPanel title="Notification preferences" icon={Bell} test_id="panel-notifications">
          {loading || !user ? (
            <PanelSkeleton rows={4} />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              <PrefBadge on={user.notifications.sms} label="SMS" />
              <PrefBadge on={user.notifications.email} label="Email" />
              <PrefBadge on={user.notifications.push} label="Push" />
              <PrefBadge on={user.notifications.marketing} label="Marketing" />
            </div>
          )}
        </DetailPanel>

        <DetailPanel title="Emergency contact" icon={AlertTriangle} test_id="panel-emergency">
          {loading || !user ? (
            <PanelSkeleton rows={2} />
          ) : user.emergency_contact ? (
            <FieldList>
              <Field label="Name" value={user.emergency_contact.name} />
              <Field label="Relationship" value={user.emergency_contact.relationship} />
              <Field label="Phone" value={user.emergency_contact.phone} mono />
            </FieldList>
          ) : (
            <PanelEmpty icon={AlertTriangle}>
              No emergency contact recorded. Walkers and boarding vendors rely on this for
              incidents.
            </PanelEmpty>
          )}
        </DetailPanel>

        <DetailPanel
          title="Addresses"
          icon={Home}
          description={user ? `${user.addresses.length} saved` : undefined}
          test_id="panel-addresses"
        >
          {loading || !user ? (
            <PanelSkeleton rows={3} />
          ) : user.addresses.length === 0 ? (
            <PanelEmpty icon={MapPin}>
              No saved addresses — this account cannot receive a delivery or a home visit.
            </PanelEmpty>
          ) : (
            <ul className="flex flex-col divide-y divide-border/60">
              {user.addresses.map((addr) => (
                <li key={addr.id} className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="text-data font-semibold">{addr.label}</span>
                    {addr.is_default && (
                      <Badge tone="primary" size="sm">
                        Default
                      </Badge>
                    )}
                  </span>
                  <span className="text-data text-muted-foreground">
                    {[addr.address, addr.landmark, addr.city, addr.state, addr.pincode]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                  {(addr.phone || addr.access_notes) && (
                    <span className="text-micro text-muted-foreground">
                      {[addr.phone, addr.access_notes].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

function PrefBadge({ on, label }: { on: boolean; label: string }) {
  return (
    <Badge tone={on ? 'success' : 'neutral'} variant={on ? 'soft' : 'outline'}>
      {on ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
      {label} {on ? 'on' : 'off'}
    </Badge>
  );
}

/* ========================================================================
   Pets
   ======================================================================== */

/**
 * The owner's animals, as cards that say something.
 *
 * The first version showed species, breed and a creation date — none of which
 * is why anyone opens this tab. What a vendor phones in about is an overdue
 * vaccination, a severe allergy or an active condition, so those lead; the
 * moderation menu is here too, because taking a profile down starts from
 * looking at it in the owner's context.
 */
function PetsTab({
  user,
  loading,
  user_id,
}: {
  user: UserDetail | undefined;
  loading: boolean;
  user_id: string;
}) {
  const navigate = useNavigate();
  const set_pet_active = use_set_pet_active();
  const [target, set_target] = useState<UserPetSummary | null>(null);
  const [modal_error, set_modal_error] = useState<string | null>(null);

  if (loading || !user) {
    return (
      <DetailPanel title="Pets" icon={PawPrint}>
        <PanelSkeleton rows={4} />
      </DetailPanel>
    );
  }
  if (user.pets.length === 0) {
    return (
      <DetailPanel title="Pets" icon={PawPrint} test_id="panel-pets">
        <PanelEmpty icon={PawPrint}>
          This account has no pets. Most services cannot be booked without one.
        </PanelEmpty>
      </DetailPanel>
    );
  }

  const hidden = user.pets.filter((p) => !p.is_active).length;
  const needs_attention = user.pets.filter(
    (p) => p.vaccination_overdue > 0 || p.active_condition_count > 0,
  ).length;

  return (
    <>
      <DetailPanel
        title="Pets"
        icon={PawPrint}
        description={[
          `${user.pets.length} registered`,
          needs_attention > 0
            ? `${needs_attention} need${needs_attention === 1 ? 's' : ''} attention`
            : null,
          hidden > 0 ? `${hidden} hidden` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        test_id="panel-pets"
        body_class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
      >
        {user.pets.map((p) => (
          <div
            key={p.id}
            className={cn(
              'flex cursor-pointer flex-col gap-2 rounded-panel bg-muted/40 p-3 transition-colors hover:bg-primary/[0.06]',
              !p.is_active && 'opacity-70',
            )}
            data-testid={`user-pet-${p.id}`}
            // The whole card opens the pet, like a table row — but a click on
            // the name link or the actions menu belongs to that control.
            onClick={(e) => {
              const el = e.target;
              if (el instanceof Element && el.closest('a,button,[role="menu"]')) return;
              if (window.getSelection()?.toString()) return;
              navigate(`/users/${user_id}/pets/${p.id}`);
            }}
          >
            <div className="flex items-start gap-3">
              {/* Photos need the admin bearer token, so they go through the
                  API client as a blob rather than a plain <img src>. */}
              {p.photo_thumb_path ? (
                <AuthImage
                  path={p.photo_thumb_path}
                  alt=""
                  className="h-11 w-11 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <PawPrint className="h-5 w-5" />
                </span>
              )}

              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex min-w-0 items-center gap-1.5">
                  <Link
                    to={`/users/${user_id}/pets/${p.id}`}
                    className="min-w-0 truncate font-semibold underline-offset-2 hover:text-primary hover:underline"
                    data-testid={`user-pet-link-${p.id}`}
                  >
                    {p.name}
                  </Link>
                  {!p.is_active && (
                    <Badge tone="destructive" size="sm">
                      Hidden
                    </Badge>
                  )}
                </div>
                <span className="truncate text-data text-muted-foreground">
                  {[title_case(p.species), p.breed_name, p.age_label].filter(Boolean).join(' · ')}
                </span>
                <span className="truncate text-micro text-muted-foreground/80">
                  {[
                    p.sex !== 'UNKNOWN' ? title_case(p.sex) : null,
                    p.neuter_status === 'NEUTERED' ? 'Neutered' : null,
                    p.weight_kg !== null ? `${p.weight_kg} kg` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No physical details recorded'}
                </span>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Actions for ${p.name}`}
                  data-testid={`pet-actions-${p.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to={`/users/${user_id}/pets/${p.id}`}>
                      <ExternalLink className="h-4 w-4" />
                      Open full record
                    </Link>
                  </DropdownMenuItem>
                  {p.microchip_id && (
                    <DropdownMenuItem
                      onSelect={() => {
                        void navigator.clipboard.writeText(p.microchip_id ?? '');
                        notify.success('Microchip ID copied');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      Copy microchip ID
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    tone={p.is_active ? 'destructive' : 'default'}
                    onSelect={() => {
                      set_modal_error(null);
                      set_target(p);
                    }}
                    data-testid={`pet-moderate-${p.id}`}
                  >
                    {p.is_active ? (
                      <>
                        <Ban className="h-4 w-4" /> Remove profile
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4" /> Restore profile
                      </>
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* The medical line. Absent badges mean "nothing flagged", which is
                the common and reassuring case — so it says so rather than
                rendering an empty row. */}
            <div className="flex flex-wrap items-center gap-1.5">
              {p.vaccination_overdue > 0 && (
                <Badge tone="destructive" size="sm">
                  <Syringe className="h-3 w-3" /> {p.vaccination_overdue} overdue
                </Badge>
              )}
              {p.active_condition_count > 0 && (
                <Badge tone="warning" size="sm">
                  <Stethoscope className="h-3 w-3" /> {p.active_condition_count} condition
                  {p.active_condition_count === 1 ? '' : 's'}
                </Badge>
              )}
              {p.allergy_count > 0 && (
                <Badge tone="warning" size="sm">
                  <AlertTriangle className="h-3 w-3" /> {p.allergy_count} allerg
                  {p.allergy_count === 1 ? 'y' : 'ies'}
                </Badge>
              )}
              {p.vaccination_overdue === 0 &&
                p.active_condition_count === 0 &&
                p.allergy_count === 0 && (
                  <Badge tone="success" size="sm">
                    <CheckCircle2 className="h-3 w-3" /> Nothing flagged
                  </Badge>
                )}
              <span className="ml-auto text-micro text-muted-foreground tabular">
                {p.booking_count} booking{p.booking_count === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        ))}
      </DetailPanel>

      <ReasonModal
        open={target !== null}
        title={
          target?.is_active
            ? `Remove ${target.name}'s profile?`
            : `Restore ${target?.name ?? 'this pet'}?`
        }
        description={
          target?.is_active
            ? `${target.name} will disappear from the owner's app, exactly as if they had deleted it. Existing bookings are unaffected.`
            : `${target?.name ?? 'This pet'} will be visible to the owner again.`
        }
        confirm_label={target?.is_active ? 'Remove profile' : 'Restore profile'}
        variant={target?.is_active ? 'destructive' : 'default'}
        loading={set_pet_active.isPending}
        error={modal_error}
        on_cancel={() => set_target(null)}
        on_confirm={(reason) => {
          if (!target) return;
          const restoring = !target.is_active;
          set_pet_active.mutate(
            { id: target.id, is_active: restoring, reason },
            {
              onSuccess: () => {
                notify.success(restoring ? `${target.name} restored` : `${target.name} removed`, {
                  description: 'The reason is recorded in the pet’s lifecycle log.',
                });
                set_target(null);
              },
              onError: (err) => {
                set_modal_error(err instanceof Error ? err.message : 'Could not update this pet');
                notify.error(err, 'Could not update this pet.');
              },
            },
          );
        }}
      />
    </>
  );
}

/* ========================================================================
   Bookings / Orders / Reviews
   ======================================================================== */

/** Payment status as a badge — null means no payment row exists yet. */
function PaymentBadge({ status, refunded }: { status: string | null; refunded?: string | null }) {
  if (!status) {
    return (
      <Badge tone="neutral" size="sm">
        Unpaid
      </Badge>
    );
  }
  const refunded_amount = Number(refunded ?? 0);
  if (refunded_amount > 0) {
    return (
      <Badge tone="warning" size="sm">
        Refunded {inr(refunded_amount)}
      </Badge>
    );
  }
  return (
    <Badge tone={status_tone(status)} size="sm">
      {title_case(status)}
    </Badge>
  );
}

function BookingsTab({
  user,
  loading,
  user_id,
}: {
  user: UserDetail | undefined;
  loading: boolean;
  user_id: string;
}) {
  const rows = user?.activity.recent_bookings ?? [];
  return (
    <DetailPanel
      title="Recent bookings"
      icon={CalendarCheck2}
      description={
        user
          ? [
              `${user.activity.bookings_total} in total`,
              user.activity.bookings_cancelled > 0
                ? `${user.activity.bookings_cancelled} cancelled or no-show`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      test_id="panel-bookings"
      body_class="p-0"
      actions={
        user && user.activity.bookings_total > 0 ? (
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/bookings?user_id=${user_id}`} data-testid="view-all-bookings">
              View all
            </Link>
          </Button>
        ) : undefined
      }
    >
      {loading || !user ? (
        <div className="p-3.5">
          <PanelSkeleton rows={5} />
        </div>
      ) : rows.length === 0 ? (
        <PanelEmpty icon={CalendarCheck2}>This account has never booked a service.</PanelEmpty>
      ) : (
        <MiniTable
          head={['When', 'Service', 'For', 'Vendor', 'Price', 'Payment', 'Status']}
          align={['left', 'left', 'left', 'left', 'right', 'left', 'left']}
          rows={rows.map((b) => ({
            key: b.id,
            to: `/bookings/${b.id}`,
            cells: [
              <span className="whitespace-nowrap">{date_time(b.slot_start)}</span>,
              title_case(b.module_type),
              // Which animal the appointment was for. A booking row without it
              // cannot answer the first question a support call asks.
              b.pet_id && b.pet_name ? (
                <Link
                  to={`/users/${user_id}/pets/${b.pet_id}`}
                  className="inline-flex items-center gap-1 underline-offset-2 hover:text-primary hover:underline"
                >
                  <PawPrint aria-hidden="true" className="h-3 w-3 text-muted-foreground" />
                  {b.pet_name}
                </Link>
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
              b.vendor_profile_id && b.vendor_business_name ? (
                <Link
                  to={`/vendors/${b.vendor_profile_id}`}
                  className="underline-offset-2 hover:text-primary hover:underline"
                >
                  {b.vendor_business_name}
                </Link>
              ) : (
                (b.vendor_business_name ?? '—')
              ),
              <span className="tabular">{inr(b.price_inr)}</span>,
              <PaymentBadge status={b.payment_status} refunded={b.refunded_inr} />,
              <span className="flex flex-col gap-0.5">
                <Badge tone={status_tone(b.status)} dot>
                  {title_case(b.status)}
                </Badge>
                {/* Why it was cancelled, inline. Otherwise every cancelled row
                    is a click into the booking to find out. */}
                {b.cancel_reason && (
                  <span className="max-w-[14rem] truncate text-micro text-muted-foreground">
                    {b.cancelled_by ? `${title_case(b.cancelled_by)}: ` : ''}
                    {b.cancel_reason}
                  </span>
                )}
              </span>,
            ],
          }))}
        />
      )}
    </DetailPanel>
  );
}

function OrdersTab({
  user,
  loading,
  user_id,
}: {
  user: UserDetail | undefined;
  loading: boolean;
  user_id: string;
}) {
  const rows = user?.activity.recent_orders ?? [];
  return (
    <DetailPanel
      title="Recent orders"
      icon={Receipt}
      description={user ? `${user.activity.orders_total} in total` : undefined}
      test_id="panel-orders"
      body_class="p-0"
      actions={
        user && user.activity.orders_total > 0 ? (
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/commerce-orders?user_id=${user_id}`} data-testid="view-all-orders">
              View all
            </Link>
          </Button>
        ) : undefined
      }
    >
      {loading || !user ? (
        <div className="p-3.5">
          <PanelSkeleton rows={4} />
        </div>
      ) : rows.length === 0 ? (
        <PanelEmpty icon={ShoppingBag}>This account has never placed a product order.</PanelEmpty>
      ) : (
        <MiniTable
          head={[
            'Placed',
            'Type',
            'Items',
            'Fulfilled by',
            'Deliver to',
            'Total',
            'Payment',
            'Status',
          ]}
          align={['left', 'left', 'right', 'left', 'left', 'right', 'left', 'left']}
          rows={rows.map((o) => ({
            key: o.id,
            to: `/commerce-orders/${o.id}`,
            cells: [
              <span className="whitespace-nowrap">{date_time(o.created_at)}</span>,
              <span className="flex flex-col gap-0.5">
                <span>{o.vertical ? title_case(o.vertical) : 'Mixed'}</span>
                {/* A prescription order that never got its script is a real
                    fulfilment blocker, so it is flagged on the row. */}
                {o.prescription_required && (
                  <Badge tone="warning" size="sm">
                    <FileText className="h-3 w-3" /> Rx
                  </Badge>
                )}
              </span>,
              <span className="tabular">{o.item_count}</span>,
              o.vendor_names.length === 0 ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span className="flex flex-col">
                  <span className="max-w-[12rem] truncate">{o.vendor_names[0]}</span>
                  {o.vendor_names.length > 1 && (
                    <span className="text-micro text-muted-foreground">
                      +{o.vendor_names.length - 1} more vendor
                      {o.vendor_names.length - 1 === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
              ),
              o.city ?? <span className="text-muted-foreground">—</span>,
              <span className="tabular">{inr(o.total_inr)}</span>,
              <PaymentBadge status={o.payment_status} />,
              <Badge tone={status_tone(o.status)} dot>
                {title_case(o.status)}
              </Badge>,
            ],
          }))}
        />
      )}
    </DetailPanel>
  );
}
function ReviewsTab({ user, loading }: { user: UserDetail | undefined; loading: boolean }) {
  const rows = user?.activity.recent_reviews ?? [];
  return (
    <DetailPanel
      title="Reviews written"
      icon={MessageSquare}
      description={user ? `${user.activity.reviews_total} in total` : undefined}
      test_id="panel-reviews"
    >
      {loading || !user ? (
        <PanelSkeleton rows={4} />
      ) : rows.length === 0 ? (
        <PanelEmpty icon={Star}>This account has not reviewed a vendor.</PanelEmpty>
      ) : (
        <ul className="flex flex-col divide-y divide-border/60">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2">
                <Stars rating={r.rating} />
                <span className="text-data font-semibold">
                  {r.vendor_business_name ?? 'Vendor'}
                </span>
                <span className="text-micro text-muted-foreground">{ago(r.created_at)}</span>
                {r.is_hidden && (
                  <Badge tone="neutral" size="sm">
                    <EyeOff className="h-3 w-3" /> Hidden
                  </Badge>
                )}
                {r.reported_at && (
                  <Badge tone="destructive" size="sm">
                    <Flag className="h-3 w-3" /> Reported
                  </Badge>
                )}
              </div>
              {r.comment && <p className="text-data text-muted-foreground">{r.comment}</p>}
            </li>
          ))}
        </ul>
      )}
    </DetailPanel>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          aria-hidden="true"
          className={cn(
            'h-3.5 w-3.5',
            n <= rating ? 'fill-warning text-warning' : 'text-muted-foreground/40',
          )}
        />
      ))}
    </span>
  );
}

/**
 * A compact table for the five-most-recent panels.
 *
 * Not the full DataTable: these lists are never sorted, paged, selected or
 * exported — they are a preview with a link to the real list. Reaching for the
 * heavyweight component here would mean a toolbar and a pagination bar around
 * five rows.
 */
function MiniTable({
  head,
  align,
  rows,
}: {
  head: string[];
  align: ('left' | 'right' | 'center')[];
  rows: { key: string; to?: string; cells: React.ReactNode[] }[];
}) {
  return (
    <div className="thin-scroll overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={h}
                scope="col"
                className={cn(
                  'border-b border-border bg-muted/50 px-3 py-1.5 text-micro font-bold uppercase tracking-wide text-muted-foreground',
                  align[i] === 'right' ? 'text-right' : 'text-left',
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="group/row [&:nth-child(even)]:bg-muted/20">
              {r.cells.map((cell, i) => (
                <td
                  key={i}
                  className={cn(
                    'border-b border-border/50 px-3 py-2 text-data',
                    align[i] === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  {/* The first cell carries the link, so the row is reachable
                      by keyboard without making the whole <tr> a tab stop. */}
                  {i === 0 && r.to ? (
                    <Link
                      to={r.to}
                      className="font-medium text-foreground underline-offset-2 hover:text-primary hover:underline"
                    >
                      {cell}
                    </Link>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
